/* global __APP_VERSION__ */
import { useEffect, useState } from 'react';

// The version baked into this bundle at build time (from package.json).
const BUILD_VERSION = __APP_VERSION__;

// How often to re-check the deployed version while the tab stays open.
const POLL_INTERVAL_MS = 15 * 60 * 1000;

async function fetchDeployedVersion() {
    try {
        // Cache-bust so we always hit the freshly deployed manifest, not a cached
        // copy held by the browser or a CDN.
        const res = await fetch(`/version.json?t=${Date.now()}`, {
            cache: 'no-store',
        });
        if (!res.ok) return null;
        const data = await res.json();
        return typeof data.version === 'string' ? data.version : null;
    } catch {
        // Offline, file missing (e.g. dev server), or malformed — just skip.
        return null;
    }
}

/**
 * Detects when a newer build of the app has been deployed by comparing the
 * version baked into this bundle against the live `/version.json`. Polls on an
 * interval and whenever the tab regains focus.
 */
export function useAppVersion() {
    const [updateAvailable, setUpdateAvailable] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const check = async () => {
            const deployed = await fetchDeployedVersion();
            if (cancelled || !deployed) return;
            if (deployed !== BUILD_VERSION) setUpdateAvailable(true);
        };

        void check();
        const interval = setInterval(check, POLL_INTERVAL_MS);
        const onVisible = () => {
            if (document.visibilityState === 'visible') void check();
        };
        document.addEventListener('visibilitychange', onVisible);

        return () => {
            cancelled = true;
            clearInterval(interval);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, []);

    return { updateAvailable };
}
